import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent
} from "react";
import { api } from "../api";
import type { FtpAccount, FtpSession, PanelUser, Site } from "../types";

type FtpTab = "manage" | "add" | "connections";
type FtpItem = FtpAccount & {
  usedBytes?: number;
  quotaMb?: number;
  directory?: string;
};

function formatBytes(bytes: number): string {
  if (!bytes || bytes < 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatQuotaMb(mb: number | undefined | null): string {
  if (!mb || mb <= 0) return "Unlimited";
  if (mb >= 1024) return `${(mb / 1024).toFixed(mb % 1024 === 0 ? 0 : 1)} GB`;
  return `${mb} MB`;
}

function genPassword(len = 18): string {
  const chars =
    "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%&*";
  const arr = new Uint8Array(len);
  crypto.getRandomValues(arr);
  return Array.from(arr, (n) => chars[n % chars.length]).join("");
}

function passwordStrength(pw: string): {
  score: number;
  label: string;
  cls: string;
} {
  if (!pw) return { score: 0, label: "", cls: "" };
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^a-zA-Z0-9]/.test(pw)) s++;
  if (s <= 2) return { score: s, label: "Weak", cls: "weak" };
  if (s <= 3) return { score: s, label: "Fair", cls: "fair" };
  if (s <= 4) return { score: s, label: "Strong", cls: "strong" };
  return { score: s, label: "Excellent", cls: "excellent" };
}

function friendlyError(raw: string): { title: string; detail: string } {
  const m = raw || "Unknown error";
  if (/not found|NOT_FOUND/i.test(m)) {
    return {
      title: "FTP account or path not found",
      detail:
        "The selected account may not be provisioned, or the FTP service could not locate the home directory."
    };
  }
  if (/LOGIN_TAKEN|already exists/i.test(m)) {
    return {
      title: "Username already exists",
      detail: "Choose a different FTP username for this account."
    };
  }
  if (/WEAK_PASSWORD|8 characters/i.test(m)) {
    return {
      title: "Password too weak",
      detail: "Use at least 8 characters with mixed types."
    };
  }
  if (/PACKAGE_LIMIT|limit/i.test(m)) {
    return {
      title: "FTP account limit reached",
      detail: m
    };
  }
  if (/AGENT|timeout|unavailable|ECONNREFUSED/i.test(m)) {
    return {
      title: "FTP Service Unavailable",
      detail:
        "The FTP service could not be reached on this server. Check the agent and FTP stack."
    };
  }
  return { title: "Could not complete request", detail: m };
}

/**
 * Premium FTP module (ftp_redesign_upgrade.md).
 * Compact header · focused workspace · Accounts | Add Account | Connections
 */
export function FtpPage({
  accounts,
  currentUser,
  sites: _sites = [],
  onRefreshAccounts,
  initialTab = "manage"
}: {
  accounts: PanelUser[];
  currentUser: PanelUser;
  sites?: Site[];
  onRefreshAccounts: () => Promise<void>;
  initialTab?: FtpTab;
}) {
  void _sites;
  const staff =
    currentUser.role === "superadmin" || currentUser.role === "admin";
  const reseller = currentUser.role === "reseller";
  const operator = staff || reseller;
  const provisioned = accounts.filter(
    (a) => a.role === "user" && a.provisionStatus === "ready"
  );

  const [userId, setUserId] = useState(
    staff
      ? provisioned[0]?.id || ""
      : reseller
        ? provisioned[0]?.id || ""
        : currentUser.provisionStatus === "ready"
          ? currentUser.id
          : ""
  );
  const [tab, setTab] = useState<FtpTab>(initialTab);
  const [items, setItems] = useState<FtpItem[]>([]);
  const [sessions, setSessions] = useState<FtpSession[]>([]);
  const [connection, setConnection] = useState<{
    host: string;
    port: number;
    protocol: string;
    note?: string;
  } | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string[]>([]);
  const [menuId, setMenuId] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  // Add form
  const [loginLocal, setLoginLocal] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [quotaMode, setQuotaMode] = useState<"unlimited" | "custom">(
    "unlimited"
  );
  const [quotaValue, setQuotaValue] = useState(1);
  const [quotaUnit, setQuotaUnit] = useState<"MB" | "GB">("GB");
  const [dirRel, setDirRel] = useState("public_html");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);

  // Success state
  const [created, setCreated] = useState<{
    login: string;
    directory: string;
    quota: string;
    password?: string;
  } | null>(null);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<FtpItem | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  // Edit drawer
  const [editAcc, setEditAcc] = useState<FtpItem | null>(null);
  const [editPass, setEditPass] = useState("");
  const [editQuotaMode, setEditQuotaMode] = useState<"unlimited" | "custom">(
    "unlimited"
  );
  const [editQuotaValue, setEditQuotaValue] = useState(1);
  const [editQuotaUnit, setEditQuotaUnit] = useState<"MB" | "GB">("GB");

  const selectedAccount = useMemo(
    () => accounts.find((a) => a.id === userId) || (!operator ? currentUser : null),
    [accounts, userId, operator, currentUser]
  );

  const homePath = useMemo(() => {
    const h =
      selectedAccount?.homePath ||
      `/home/${selectedAccount?.username || "user"}`;
    return h.endsWith("/") ? h : `${h}/`;
  }, [selectedAccount]);

  const finalDir = useMemo(() => {
    const rel = dirRel.trim().replace(/^\/+/, "");
    if (!rel || rel === ".") return homePath.replace(/\/$/, "");
    return `${homePath}${rel}`.replace(/\/+/g, "/");
  }, [homePath, dirRel]);

  const broadAccess = useMemo(() => {
    const rel = dirRel.trim().replace(/^\/+/, "");
    return !rel || rel === "." || rel === "/";
  }, [dirRel]);

  const diskLimitMb = useMemo(() => {
    // Prefer explicit quota on account if present; else package-like soft limit unknown
    const q = (selectedAccount as { quotaDiskMb?: number | null })?.quotaDiskMb;
    if (q != null && q > 0) return q;
    return 0; // 0 = unknown / no hard check
  }, [selectedAccount]);

  const quotaMbComputed = useMemo(() => {
    if (quotaMode === "unlimited") return 0;
    return quotaUnit === "GB" ? quotaValue * 1024 : quotaValue;
  }, [quotaMode, quotaUnit, quotaValue]);

  const quotaExceeds =
    diskLimitMb > 0 &&
    quotaMode === "custom" &&
    quotaMbComputed > diskLimitMb;

  const strength = passwordStrength(password);
  const passwordsMatch =
    password.length > 0 && password2.length > 0 && password === password2;
  const passwordsMismatch =
    password2.length > 0 && password !== password2;

  const loginValid = useMemo(() => {
    if (!loginLocal.trim()) return { ok: true, msg: "Blank → main account user" };
    if (loginLocal.length > 32)
      return { ok: false, msg: "Username too long (max 32)" };
    if (!/^[a-zA-Z][a-zA-Z0-9._-]*$/.test(loginLocal))
      return { ok: false, msg: "Invalid characters" };
    const taken = items.some(
      (i) =>
        i.login === loginLocal.toLowerCase() ||
        i.login.endsWith(`_${loginLocal.toLowerCase()}`)
    );
    if (taken) return { ok: false, msg: "Already exists" };
    return { ok: true, msg: "Valid" };
  }, [loginLocal, items]);

  useEffect(() => {
    if (!userId && provisioned[0]?.id) setUserId(provisioned[0].id);
  }, [provisioned, userId]);

  useEffect(() => {
    setTab(initialTab);
    setCreated(null);
  }, [initialTab]);

  const load = useCallback(async () => {
    if (!userId && !staff) {
      setLoading(false);
      return;
    }
    setError("");
    setLoading(true);
    try {
      const q = userId ? `?userId=${encodeURIComponent(userId)}` : "";
      const [acc, conn, sess] = await Promise.all([
        api<{ items: FtpItem[] }>(`/api/v1/ftp/accounts${q}`),
        userId
          ? api<{ connection: typeof connection }>(
              `/api/v1/ftp/connection?userId=${encodeURIComponent(userId)}`
            ).catch(() => ({ connection: null }))
          : Promise.resolve({ connection: null }),
        userId
          ? api<{ items: FtpSession[] }>(
              `/api/v1/ftp/sessions?userId=${encodeURIComponent(userId)}`
            ).catch(() => ({ items: [] as FtpSession[] }))
          : Promise.resolve({ items: [] as FtpSession[] })
      ]);
      setItems(acc.items || []);
      setConnection(conn.connection || null);
      setSessions(sess.items || []);
    } catch (err) {
      const fe = friendlyError(
        err instanceof Error ? err.message : String(err)
      );
      setError(`${fe.title}: ${fe.detail}`);
    } finally {
      setLoading(false);
    }
  }, [userId, staff]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter(
      (i) =>
        i.login.toLowerCase().includes(q) ||
        (i.directory || "").toLowerCase().includes(q) ||
        (i.username || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const resetForm = () => {
    setLoginLocal("");
    setPassword("");
    setPassword2("");
    setShowPass(false);
    setQuotaMode("unlimited");
    setQuotaValue(1);
    setQuotaUnit("GB");
    setDirRel("public_html");
    setShowAdvanced(false);
  };

  const createAccount = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!userId) {
      setError("Select a hosting account first.");
      return;
    }
    if (!loginValid.ok) {
      setError(loginValid.msg);
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== password2) {
      setError("Passwords do not match.");
      return;
    }
    if (quotaExceeds) {
      setError("Quota exceeds hosting account storage limit.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const pathRestriction = dirRel.trim().replace(/^\/+/, "") || ".";
      const res = await api<{
        account: FtpItem;
        password?: string;
      }>("/api/v1/ftp/accounts", {
        method: "POST",
        body: JSON.stringify({
          userId,
          login: loginLocal.trim() || undefined,
          password,
          pathRestriction,
          quotaMb: quotaMbComputed,
          quotaUnlimited: quotaMode === "unlimited"
        })
      });
      setCreated({
        login: res.account.login,
        directory: pathRestriction === "." ? "home" : pathRestriction,
        quota: formatQuotaMb(quotaMbComputed),
        password: res.password || password
      });
      resetForm();
      await load();
    } catch (err) {
      const fe = friendlyError(
        err instanceof Error ? err.message : String(err)
      );
      setError(`${fe.title}: ${fe.detail}`);
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    setError("");
    try {
      await api(`/api/v1/ftp/accounts/${deleteTarget.id}`, {
        method: "DELETE"
      });
      setDeleteTarget(null);
      setMenuId(null);
      await load();
    } catch (err) {
      const fe = friendlyError(
        err instanceof Error ? err.message : String(err)
      );
      setError(`${fe.title}: ${fe.detail}`);
    } finally {
      setBusy(false);
    }
  };

  const confirmBulkDelete = async () => {
    if (!selected.length) return;
    setBusy(true);
    setError("");
    try {
      await api("/api/v1/ftp/accounts/bulk-delete", {
        method: "POST",
        body: JSON.stringify({ ids: selected })
      });
      setSelected([]);
      setBulkDeleteOpen(false);
      await load();
    } catch (err) {
      const fe = friendlyError(
        err instanceof Error ? err.message : String(err)
      );
      setError(`${fe.title}: ${fe.detail}`);
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editAcc) return;
    setBusy(true);
    setError("");
    const qMb =
      editQuotaMode === "unlimited"
        ? 0
        : editQuotaUnit === "GB"
          ? editQuotaValue * 1024
          : editQuotaValue;
    try {
      await api(`/api/v1/ftp/accounts/${editAcc.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          quotaMb: qMb,
          password: editPass || undefined
        })
      });
      setEditAcc(null);
      setEditPass("");
      await load();
    } catch (err) {
      const fe = friendlyError(
        err instanceof Error ? err.message : String(err)
      );
      setError(`${fe.title}: ${fe.detail}`);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async (pid: number | null) => {
    if (!pid) return;
    setBusy(true);
    try {
      await api("/api/v1/ftp/sessions/disconnect", {
        method: "POST",
        body: JSON.stringify({ userId, pid })
      });
      await load();
    } catch (err) {
      const fe = friendlyError(
        err instanceof Error ? err.message : String(err)
      );
      setError(`${fe.title}: ${fe.detail}`);
    } finally {
      setBusy(false);
    }
  };

  const copyText = async (t: string) => {
    try {
      await navigator.clipboard.writeText(t);
    } catch {
      /* ignore */
    }
  };

  const pageTitle =
    tab === "add"
      ? "Add FTP Account"
      : tab === "connections"
        ? "FTP Connections"
        : "FTP Accounts";

  const pageSub =
    tab === "add"
      ? "Create a restricted FTP account for a hosting account."
      : tab === "connections"
        ? "Monitor active FTP sessions and disconnect users."
        : "Manage FTP users, access directories and quotas.";

  const crumb =
    tab === "add"
      ? "FTP / Add Account"
      : tab === "connections"
        ? "FTP / Connections"
        : "FTP / Accounts";

  if (!userId && !staff && !reseller) {
    return (
      <div className="ftp-page">
        <div className="ftp-head">
          <div>
            <p className="ftp-crumb">FTP</p>
            <h1 className="ftp-title">FTP Accounts</h1>
            <p className="ftp-sub">
              Your hosting account must be ready before FTP is available.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => void onRefreshAccounts()}
          >
            Refresh
          </button>
        </div>
        <div className="rp-empty">
          <strong>No ready account</strong>
          <p>Finish account setup, then manage FTP users here.</p>
        </div>
      </div>
    );
  }

  const tabs: { id: FtpTab; label: string }[] = [
    { id: "manage", label: "Accounts" },
    { id: "add", label: "Add Account" },
    { id: "connections", label: "Connections" }
  ];

  return (
    <div className="ftp-page">
      {/* Compact header */}
      <div className="ftp-head">
        <div className="ftp-head-main">
          <p className="ftp-crumb">{crumb}</p>
          <div className="ftp-title-row">
            <h1 className="ftp-title">{pageTitle}</h1>
            {selectedAccount ? (
              <span className="ftp-ctx">
                Account: <strong>{selectedAccount.username}</strong>
                <span className="ftp-ctx-meta">
                  {operator
                    ? staff
                      ? "WHM / root"
                      : "Reseller WHM"
                    : "cPanel"}
                </span>
              </span>
            ) : null}
          </div>
          <p className="ftp-sub">{pageSub}</p>
        </div>
        <div className="ftp-head-actions">
          {operator ? (
            <select
              className="ftp-acct-select"
              value={userId}
              onChange={(e) => {
                setUserId(e.target.value);
                setSelected([]);
                setCreated(null);
              }}
              aria-label="Hosting account"
            >
              {staff ? <option value="">Select account…</option> : null}
              {provisioned.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.username}
                  {a.primaryDomain ? ` · ${a.primaryDomain}` : ""}
                </option>
              ))}
            </select>
          ) : null}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => void load()}
            disabled={busy || loading}
          >
            Refresh
          </button>
          {tab === "manage" ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              style={{ width: "auto", marginTop: 0 }}
              onClick={() => {
                setCreated(null);
                setTab("add");
              }}
              disabled={!userId && staff}
            >
              + Add FTP Account
            </button>
          ) : null}
        </div>
      </div>

      {/* Segment tabs */}
      <div className="ftp-seg" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`ftp-seg-btn${tab === t.id ? " is-active" : ""}`}
            onClick={() => {
              setTab(t.id);
              setCreated(null);
              setError("");
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error ? (
        <div className="ftp-alert ftp-alert-error" role="alert">
          <div>
            <strong>{error.split(":")[0]}</strong>
            <p>{error.includes(":") ? error.slice(error.indexOf(":") + 1).trim() : error}</p>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setError("")}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {/* ── Accounts ── */}
      {tab === "manage" ? (
        <>
          <div className="ftp-toolbar">
            <input
              className="ftp-search"
              placeholder="Search username or directory…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {selected.length > 0 ? (
              <button
                type="button"
                className="btn btn-ghost btn-sm ftp-danger-btn"
                disabled={busy}
                onClick={() => setBulkDeleteOpen(true)}
              >
                Delete Selected ({selected.length})
              </button>
            ) : (
              <span className="ftp-toolbar-meta">
                {filtered.length} account{filtered.length === 1 ? "" : "s"}
              </span>
            )}
          </div>

          <div className="ftp-card">
            {loading ? (
              <div className="ftp-skeleton">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="ftp-skel-row" />
                ))}
              </div>
            ) : (
              <table className="ftp-table">
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>
                      <input
                        type="checkbox"
                        checked={
                          filtered.length > 0 &&
                          selected.length === filtered.length
                        }
                        onChange={() =>
                          setSelected((s) =>
                            s.length === filtered.length
                              ? []
                              : filtered.map((i) => i.id)
                          )
                        }
                        aria-label="Select all"
                      />
                    </th>
                    <th>Username</th>
                    <th>Account</th>
                    <th>Directory</th>
                    <th>Quota</th>
                    <th>Usage</th>
                    <th>Status</th>
                    <th>Last connection</th>
                    <th style={{ width: 48 }} />
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={9}>
                        <div className="ftp-empty">
                          <strong>No FTP accounts</strong>
                          <p>
                            Create an FTP user to upload with FileZilla or any
                            FTP client.
                          </p>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            style={{ width: "auto", marginTop: 0 }}
                            onClick={() => setTab("add")}
                          >
                            + Add FTP Account
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filtered.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selected.includes(a.id)}
                            onChange={() =>
                              setSelected((cur) =>
                                cur.includes(a.id)
                                  ? cur.filter((x) => x !== a.id)
                                  : [...cur, a.id]
                              )
                            }
                          />
                        </td>
                        <td>
                          <strong className="mono">{a.login}</strong>
                        </td>
                        <td className="faint">{a.username || "—"}</td>
                        <td className="mono ftp-dir-cell">
                          {a.pathRestriction === "." || !a.pathRestriction
                            ? "home"
                            : a.pathRestriction}
                        </td>
                        <td>{formatQuotaMb(a.quotaMb)}</td>
                        <td className="mono">{formatBytes(a.usedBytes || 0)}</td>
                        <td>
                          <span
                            className={`ftp-badge ${
                              a.status === "active"
                                ? "ftp-badge-ok"
                                : "ftp-badge-muted"
                            }`}
                          >
                            <span className="ftp-badge-dot" />
                            {a.status === "active"
                              ? a.lastLoginAt
                                ? "Active"
                                : "Never Used"
                              : "Suspended"}
                          </span>
                        </td>
                        <td className="faint" style={{ fontSize: 12 }}>
                          {a.lastLoginAt
                            ? new Date(a.lastLoginAt).toLocaleString()
                            : "—"}
                        </td>
                        <td>
                          <div className="rp-menu-wrap">
                            <button
                              type="button"
                              className="rp-menu-btn"
                              onClick={() =>
                                setMenuId((id) =>
                                  id === a.id ? null : a.id
                                )
                              }
                            >
                              •••
                            </button>
                            {menuId === a.id ? (
                              <div className="rp-menu">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setMenuId(null);
                                    setEditAcc(a);
                                    setEditQuotaMode(
                                      !a.quotaMb ? "unlimited" : "custom"
                                    );
                                    if (a.quotaMb && a.quotaMb >= 1024) {
                                      setEditQuotaUnit("GB");
                                      setEditQuotaValue(
                                        Math.round((a.quotaMb / 1024) * 10) /
                                          10
                                      );
                                    } else {
                                      setEditQuotaUnit("MB");
                                      setEditQuotaValue(a.quotaMb || 1000);
                                    }
                                    setEditPass("");
                                  }}
                                >
                                  Change password / quota
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setMenuId(null);
                                    setDeleteTarget(a);
                                  }}
                                >
                                  Delete account
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>

          {connection ? (
            <p className="ftp-conn-hint">
              Connect with{" "}
              <strong className="mono">
                {connection.protocol?.toUpperCase() || "FTP"}
              </strong>{" "}
              · host{" "}
              <span className="mono">{connection.host}</span> · port{" "}
              <span className="mono">{connection.port}</span>
            </p>
          ) : null}
        </>
      ) : null}

      {/* ── Add Account ── */}
      {tab === "add" ? (
        created ? (
          <div className="ftp-success-card">
            <div className="ftp-success-icon" aria-hidden>
              ✓
            </div>
            <h2>FTP Account Created</h2>
            <p className="mono ftp-success-login">{created.login}</p>
            <dl className="ftp-success-meta">
              <div>
                <dt>Directory</dt>
                <dd className="mono">{created.directory}</dd>
              </div>
              <div>
                <dt>Quota</dt>
                <dd>{created.quota}</dd>
              </div>
              {created.password ? (
                <div>
                  <dt>Password</dt>
                  <dd>
                    <span className="mono">{created.password}</span>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => void copyText(created.password!)}
                    >
                      Copy
                    </button>
                  </dd>
                </div>
              ) : null}
            </dl>
            <div className="ftp-success-actions">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                style={{ width: "auto", marginTop: 0 }}
                onClick={() => {
                  setCreated(null);
                  setTab("manage");
                }}
              >
                View FTP Accounts
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setCreated(null)}
              >
                Create Another
              </button>
            </div>
          </div>
        ) : (
          <div className="ftp-add-layout">
            <form
              className="ftp-form-card"
              onSubmit={(e) => void createAccount(e)}
              id="ftp-create-form"
            >
              <div className="ftp-form-section">
                <h2>FTP Account</h2>
                <p>Create credentials and define access.</p>
              </div>

              <div className="ftp-field">
                <label htmlFor="ftp-login">Username</label>
                <input
                  id="ftp-login"
                  value={loginLocal}
                  onChange={(e) => setLoginLocal(e.target.value)}
                  placeholder="admin"
                  autoComplete="off"
                  className={
                    loginLocal && !loginValid.ok ? "is-invalid" : ""
                  }
                />
                <span
                  className={`ftp-hint ${
                    loginLocal && !loginValid.ok ? "is-err" : ""
                  }`}
                >
                  {loginLocal
                    ? loginValid.msg
                    : "Leave blank to use the main account user."}
                </span>
              </div>

              <div className="ftp-field">
                <label>Hosting account</label>
                <div className="ftp-readonly mono">
                  {selectedAccount?.username || "—"}
                  {selectedAccount?.primaryDomain
                    ? ` · ${selectedAccount.primaryDomain}`
                    : ""}
                </div>
              </div>

              <div className="ftp-field">
                <label htmlFor="ftp-pass">Password</label>
                <div className="ftp-pass-row">
                  <input
                    id="ftp-pass"
                    type={showPass ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setShowPass((v) => !v)}
                  >
                    {showPass ? "Hide" : "Show"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      const p = genPassword();
                      setPassword(p);
                      setPassword2(p);
                      setShowPass(true);
                    }}
                  >
                    Generate
                  </button>
                  {password ? (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => void copyText(password)}
                    >
                      Copy
                    </button>
                  ) : null}
                </div>
                {password ? (
                  <div className={`ftp-strength ftp-strength-${strength.cls}`}>
                    <div className="ftp-strength-bar">
                      <span style={{ width: `${(strength.score / 5) * 100}%` }} />
                    </div>
                    <span>{strength.label}</span>
                  </div>
                ) : null}
              </div>

              <div className="ftp-field">
                <label htmlFor="ftp-pass2">Confirm password</label>
                <input
                  id="ftp-pass2"
                  type={showPass ? "text" : "password"}
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className={passwordsMismatch ? "is-invalid" : ""}
                />
                {passwordsMatch ? (
                  <span className="ftp-hint is-ok">✓ Passwords match</span>
                ) : passwordsMismatch ? (
                  <span className="ftp-hint is-err">
                    ✕ Passwords do not match
                  </span>
                ) : null}
              </div>

              <div className="ftp-field">
                <label>Storage quota</label>
                <div className="ftp-seg-inline" role="group">
                  <button
                    type="button"
                    className={`ftp-seg-btn${
                      quotaMode === "unlimited" ? " is-active" : ""
                    }`}
                    onClick={() => setQuotaMode("unlimited")}
                  >
                    Unlimited
                  </button>
                  <button
                    type="button"
                    className={`ftp-seg-btn${
                      quotaMode === "custom" ? " is-active" : ""
                    }`}
                    onClick={() => setQuotaMode("custom")}
                  >
                    Custom
                  </button>
                </div>
                {quotaMode === "custom" ? (
                  <div className="ftp-quota-row">
                    <input
                      type="number"
                      min={1}
                      step={quotaUnit === "GB" ? 0.5 : 1}
                      value={quotaValue}
                      onChange={(e) =>
                        setQuotaValue(Number(e.target.value) || 0)
                      }
                    />
                    <select
                      value={quotaUnit}
                      onChange={(e) =>
                        setQuotaUnit(e.target.value as "MB" | "GB")
                      }
                    >
                      <option value="MB">MB</option>
                      <option value="GB">GB</option>
                    </select>
                  </div>
                ) : null}
                <span
                  className={`ftp-hint ${quotaExceeds ? "is-err" : ""}`}
                >
                  {quotaExceeds
                    ? "Quota exceeds hosting account storage limit."
                    : "Maximum storage available to this FTP account."}
                </span>
              </div>

              <div className="ftp-field">
                <label>Access directory</label>
                <div className="ftp-dir-input">
                  <span className="ftp-dir-base mono" title={homePath}>
                    {homePath}
                  </span>
                  <input
                    value={dirRel}
                    onChange={(e) => setDirRel(e.target.value)}
                    placeholder="public_html"
                  />
                </div>
                <div className="ftp-final-path">
                  <span className="ftp-final-label">Final FTP root</span>
                  <code className="mono">{finalDir}</code>
                </div>
                <p className="ftp-hint">
                  FTP access is restricted to this directory.
                </p>
                {broadAccess ? (
                  <div className="ftp-alert ftp-alert-warn">
                    <strong>Security warning</strong>
                    <p>
                      This directory provides broader access to the hosting
                      account home.
                    </p>
                  </div>
                ) : null}
              </div>

              <details
                className="ftp-advanced"
                open={showAdvanced}
                onToggle={(e) =>
                  setShowAdvanced((e.target as HTMLDetailsElement).open)
                }
              >
                <summary>Advanced FTP Settings</summary>
                <p className="ftp-hint" style={{ marginTop: 10 }}>
                  Read-only mode, IP restrictions and connection limits will
                  appear here in a future update.
                </p>
              </details>

              <div className="ftp-form-actions ftp-form-actions-inline">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    resetForm();
                    setTab("manage");
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ width: "auto", marginTop: 0, minHeight: 44 }}
                  disabled={
                    busy ||
                    !userId ||
                    !loginValid.ok ||
                    passwordsMismatch ||
                    quotaExceeds ||
                    password.length < 8
                  }
                >
                  {busy ? "Creating FTP Account…" : "Create FTP Account"}
                </button>
              </div>
            </form>

            {/* Right summary */}
            <aside className={`ftp-summary${summaryOpen ? " is-open" : ""}`}>
              <button
                type="button"
                className="ftp-summary-toggle"
                onClick={() => setSummaryOpen((v) => !v)}
              >
                FTP Account Summary
                <span aria-hidden>{summaryOpen ? "▴" : "▾"}</span>
              </button>
              <div className="ftp-summary-body">
                <h3>FTP Account Summary</h3>
                <dl>
                  <div>
                    <dt>Account</dt>
                    <dd>{selectedAccount?.username || "—"}</dd>
                  </div>
                  <div>
                    <dt>Username</dt>
                    <dd className="mono">
                      {loginLocal.trim() || selectedAccount?.username || "—"}
                    </dd>
                  </div>
                  <div>
                    <dt>Quota</dt>
                    <dd>
                      {quotaMode === "unlimited"
                        ? "Unlimited"
                        : `${quotaValue} ${quotaUnit}`}
                    </dd>
                  </div>
                  <div>
                    <dt>Directory</dt>
                    <dd className="mono">
                      {dirRel.trim() || "home"}
                    </dd>
                  </div>
                  <div>
                    <dt>Access</dt>
                    <dd>{broadAccess ? "Home (broad)" : "Restricted"}</dd>
                  </div>
                </dl>
                {connection ? (
                  <div className="ftp-summary-conn">
                    <span className="faint">Connect to</span>
                    <code className="mono">
                      {connection.host}:{connection.port}
                    </code>
                  </div>
                ) : null}
              </div>
            </aside>

            {/* Sticky mobile / desktop bar */}
            <div className="ftp-sticky-bar">
              <span className="ftp-sticky-meta">
                {password || loginLocal || dirRel !== "public_html"
                  ? "Unsaved changes"
                  : "Ready to create"}
              </span>
              <div className="ftp-sticky-actions">
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    resetForm();
                    setTab("manage");
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="ftp-create-form"
                  className="btn btn-primary btn-sm"
                  style={{ width: "auto", marginTop: 0, minHeight: 44 }}
                  disabled={
                    busy ||
                    !userId ||
                    !loginValid.ok ||
                    passwordsMismatch ||
                    quotaExceeds ||
                    password.length < 8
                  }
                >
                  {busy ? "Creating…" : "Create FTP Account"}
                </button>
              </div>
            </div>
          </div>
        )
      ) : null}

      {/* ── Connections ── */}
      {tab === "connections" ? (
        <>
          <div className="ftp-summary-strip">
            <div className="ftp-sum">
              <span className="ftp-sum-label">Active Connections</span>
              <span className="ftp-sum-value">{sessions.length}</span>
            </div>
            <div className="ftp-sum">
              <span className="ftp-sum-label">Users Connected</span>
              <span className="ftp-sum-value">
                {new Set(sessions.map((s) => s.user).filter(Boolean)).size}
              </span>
            </div>
            <div className="ftp-sum">
              <span className="ftp-sum-label">FTP Host</span>
              <span className="ftp-sum-value" style={{ fontSize: 15 }}>
                {connection?.host || "—"}
              </span>
            </div>
            <div className="ftp-sum">
              <span className="ftp-sum-label">Port</span>
              <span className="ftp-sum-value">
                {connection?.port ?? 21}
              </span>
            </div>
          </div>

          <div className="ftp-card">
            <table className="ftp-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>IP Address</th>
                  <th>Connection</th>
                  <th>Started</th>
                  <th>Status</th>
                  <th>Process</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="faint">
                      Loading sessions…
                    </td>
                  </tr>
                ) : sessions.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="ftp-empty-cell">
                      There are no active FTP connections to your server
                    </td>
                  </tr>
                ) : (
                  sessions.map((s, i) => (
                    <tr key={`${s.pid}-${i}`}>
                      <td className="mono">{s.user}</td>
                      <td className="mono">{s.clientIp}</td>
                      <td className="faint">
                        {(s.protocol || "ftp").toUpperCase()}
                      </td>
                      <td className="faint">{s.loginTime}</td>
                      <td>
                        <span className="ftp-badge ftp-badge-ok">
                          <span className="ftp-badge-dot" />
                          {s.status}
                        </span>
                      </td>
                      <td className="mono">{s.pid ?? "—"}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={!s.pid || busy}
                          onClick={() => void disconnect(s.pid)}
                        >
                          Disconnect
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      {/* Delete confirm */}
      {deleteTarget ? (
        <div
          className="rp-drawer-bg"
          onClick={() => !busy && setDeleteTarget(null)}
        >
          <div
            className="ftp-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Delete FTP account"
          >
            <h2>Delete FTP Account?</h2>
            <p>
              This will remove{" "}
              <strong className="mono">{deleteTarget.login}</strong> and revoke
              its access. Files in the directory will not be deleted unless
              explicitly selected.
            </p>
            <div className="ftp-modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary ftp-btn-danger"
                style={{ width: "auto", marginTop: 0 }}
                disabled={busy}
                onClick={() => void confirmDelete()}
              >
                {busy ? "Deleting…" : "Delete FTP Account"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {bulkDeleteOpen ? (
        <div
          className="rp-drawer-bg"
          onClick={() => !busy && setBulkDeleteOpen(false)}
        >
          <div
            className="ftp-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            <h2>Delete {selected.length} FTP accounts?</h2>
            <p>
              Selected accounts will lose FTP access. Website files are not
              removed.
            </p>
            <div className="ftp-modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setBulkDeleteOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary ftp-btn-danger"
                style={{ width: "auto", marginTop: 0 }}
                disabled={busy}
                onClick={() => void confirmBulkDelete()}
              >
                {busy ? "Deleting…" : "Delete FTP Accounts"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Edit drawer */}
      {editAcc ? (
        <div className="rp-drawer-bg" onClick={() => !busy && setEditAcc(null)}>
          <div
            className="rp-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            <div className="rp-drawer-hd">
              <div>
                <h2 className="mono">{editAcc.login}</h2>
                <p>Change password or quota</p>
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setEditAcc(null)}
              >
                ✕
              </button>
            </div>
            <form className="rp-drawer-body" onSubmit={saveEdit}>
              <div className="rp-drawer-section">
                <div className="ftp-field">
                  <label>New password (optional)</label>
                  <input
                    type="password"
                    value={editPass}
                    onChange={(e) => setEditPass(e.target.value)}
                    minLength={8}
                    placeholder="Leave blank to keep"
                  />
                </div>
                <div className="ftp-field" style={{ marginTop: 12 }}>
                  <label>Quota</label>
                  <div className="ftp-seg-inline">
                    <button
                      type="button"
                      className={`ftp-seg-btn${
                        editQuotaMode === "unlimited" ? " is-active" : ""
                      }`}
                      onClick={() => setEditQuotaMode("unlimited")}
                    >
                      Unlimited
                    </button>
                    <button
                      type="button"
                      className={`ftp-seg-btn${
                        editQuotaMode === "custom" ? " is-active" : ""
                      }`}
                      onClick={() => setEditQuotaMode("custom")}
                    >
                      Custom
                    </button>
                  </div>
                  {editQuotaMode === "custom" ? (
                    <div className="ftp-quota-row" style={{ marginTop: 8 }}>
                      <input
                        type="number"
                        min={1}
                        value={editQuotaValue}
                        onChange={(e) =>
                          setEditQuotaValue(Number(e.target.value) || 0)
                        }
                      />
                      <select
                        value={editQuotaUnit}
                        onChange={(e) =>
                          setEditQuotaUnit(e.target.value as "MB" | "GB")
                        }
                      >
                        <option value="MB">MB</option>
                        <option value="GB">GB</option>
                      </select>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="rp-drawer-ft">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setEditAcc(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ width: "auto", marginTop: 0 }}
                  disabled={busy}
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
